import React, { useEffect, useState, useMemo } from "react";
import { motion } from "framer-motion";
import { FaEnvelope, FaKey, FaArrowLeft, FaShieldAlt, FaSync } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Alert from "../components/Alert";

export default function ForgetOtp() {
  const navigate = useNavigate();
  const { alert, showAlert, verifyForgotOtp, authLoading, requestForgotOtp } = useAuth();

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const saved = localStorage.getItem("forgotEmail") || "";
    setEmail(saved);
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const cleanEmail = useMemo(
    () => String(email || "").trim().toLowerCase(),
    [email]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanOtp = String(otp || "").trim();

    if (!cleanEmail || !cleanEmail.includes("@")) {
      showAlert("error", "Valid email is required");
      return;
    }

    if (!/^\d{6}$/.test(cleanOtp)) {
      showAlert("error", "OTP must be 6 digits");
      return;
    }

    await verifyForgotOtp(cleanEmail, cleanOtp);
  };

  const handleResend = async () => {
    if (!cleanEmail || !cleanEmail.includes("@")) {
      showAlert("error", "Valid email is required");
      return;
    }
    if (cooldown > 0) return;

    const ok = await requestForgotOtp(cleanEmail);
    if (ok) {
      setCooldown(30);
      setOtp("");
    }
  };

  return (
    <div className="min-h-screen bg-[#07111F] text-white font-sans relative overflow-hidden flex items-center justify-center px-6">
      
      {/* Background Accents */}
     <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-700/20 blur-[140px] rounded-full -translate-y-1/2 translate-x-1/2" />

<div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-yellow-400/10 blur-[140px] rounded-full translate-y-1/2 -translate-x-1/2" />
      <Alert
        type={alert.type}
        message={alert.message}
        isOpen={alert.isOpen}
        onClose={() => showAlert({ isOpen: false, type: "", message: "" })}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
className="relative z-10 w-full max-w-md bg-[#0B1D34] border border-blue-800/40 rounded-[2.5rem] p-8 md:p-10 shadow-[0_30px_80px_rgba(0,0,0,.55)] backdrop-blur-xl"      >
        {/* Header Navigation */}
        <div className="flex items-center justify-between mb-8">
          <button
            type="button"
            onClick={() => navigate("/forget-password")}
            className="inline-flex items-center gap-2 text-[10px] font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest transition-colors"
          >
            <FaArrowLeft size={10} /> Back
          </button>
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">3Workers Security</div>
        </div>

        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 mx-auto bg-yellow-400 text-[#07111F] rounded-2xl flex items-center justify-center mb-6 shadow-xl shadow-yellow-500/20">
            <FaShieldAlt size={24} />
          </div>
          <h2 className="text-3xl font-black tracking-tight uppercase text-white">
            Verify Node
          </h2>
          <p className="text-slate-300 text-sm mt-2 font-medium leading-relaxed">
            A 6-digit authentication code has been sent to your email.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Display (Read-only feel) */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase text-slate-300 ml-1">Identity</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 transition-colors">
                <FaEnvelope size={12} />
              </div>
              <input
                readOnly
                value={email}
className="w-full pl-11 pr-4 py-4 bg-[#071523] border border-blue-800/40 rounded-2xl text-sm text-slate-300 outline-none cursor-not-allowed"              />
            </div>
          </div>

          {/* OTP Input */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-black uppercase text-slate-400 ml-1">Authentication OTP</label>
            <div className="relative group">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-indigo-500 transition-colors">
                <FaKey size={12} />
              </div>
              <input
                autoFocus
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full pl-11 pr-4 py-4 bg-[#071523] border border-blue-800/40 rounded-2xl text-lg text-white placeholder:text-slate-500 focus:bg-[#102744] focus:border-yellow-400 focus:ring-4 focus:ring-yellow-400/20 outline-none transition-all tracking-[0.5em] font-black"
                placeholder="000000"
              />
            </div>
          </div>

          {/* Resend & Action Row */}
          <div className="flex flex-col gap-4 pt-2">
            <motion.button
              type="submit"
              disabled={authLoading}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="w-full py-4 bg-slate-900 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-slate-00 hover:bg-indigo-600 transition-all flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {authLoading ? "Decrypting..." : "Verify Identity"}
            </motion.button>

            <button
              type="button"
              onClick={handleResend}
              disabled={authLoading || cooldown > 0}
              className="flex items-center justify-center gap-2 text-[10px] font-black text-yellow-400 hover:text-yellow-300 uppercase tracking-widest disabled:text-slate-300 transition-colors"
            >
              <FaSync className={cooldown > 0 ? "animate-spin" : ""} size={10} />
              {cooldown > 0 ? `Resend available in ${cooldown}s` : "Request New OTP"}
            </button>
          </div>
        </form>

       
      </motion.div>
    </div>
  );
}